import * as Sentry from '@sentry/nextjs'
// `\` and C0 controls make WHATWG URL parsing resolve off-origin — see the
// rationale on the shared constant in ./apiPath.
import { UNSAFE_PATH_CHARS } from './apiPath'

/**
 * Shared navigation-target validators. Every string that reaches
 * `router.push`/`router.replace`/`window.open`/`window.location` from an
 * untrusted source — CMS/cinematics config, query params, cookies, profile
 * data — must pass through one of these first. Two philosophies:
 *
 * - `toSafeHttpUrl`   — scheme allowlist, any origin. For CMS "external link"
 *                       fields that may legitimately point off-site.
 * - `toSafeInternalPath` — same-origin path only. For redirect/referrer
 *                       targets that must never leave the app origin.
 *
 * Both resolve against the current origin, so they run client-side. A missing
 * `window` (SSR) yields `null` rather than throwing.
 */

const getOrigin = (): string | null =>
  typeof window !== 'undefined' ? window.location.origin : null

/**
 * Parse `raw` and return it only if it is an http(s) URL. Any other scheme
 * (`javascript:`, `data:`, …), a malformed URL, or SSR (no `window`) yields
 * `null`. Malformed input reports to Sentry as a warning — a CMS field that
 * can't parse is a config mistake worth seeing.
 */
export const toSafeHttpUrl = (raw: string): URL | null => {
  const origin = getOrigin()
  if (!origin) return null
  try {
    const url = new URL(raw, origin)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url : null
  } catch (error) {
    Sentry.captureException(error, { level: 'warning' })
    return null
  }
}

/**
 * Return a site-relative path (`pathname + search + hash`) only if `raw`
 * resolves to the current origin; otherwise `null`. Rejects absolute/
 * protocol-relative/off-origin targets and control-char bypasses. Use for
 * `?referrer` and any config-driven redirect that must stay in-app.
 */
export const toSafeInternalPath = (raw: string | null | undefined): string | null => {
  if (!raw || UNSAFE_PATH_CHARS.test(raw)) return null
  const origin = getOrigin()
  if (!origin) return null
  try {
    const url = new URL(raw, origin)
    if (url.origin !== origin) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

export type NavTarget = { url: string; external: boolean } | null

/**
 * Resolve a CMS/config link into a navigation decision: same-origin links
 * become site-relative in-place targets, other valid http(s) links open
 * external, and anything ambiguous or non-http yields `null` (report +
 * fall through at the call site). Mirrors the customButton-link contract
 * from `Cards/navigation.ts` so every config-driven sink shares one check.
 */
// Non-http(s) schemes that are safe to hand to window.location — they open a
// mail/phone handler, never execute script (unlike javascript:/data:). CMS
// banner/modal actions legitimately use these, so pass them through verbatim.
const SAFE_EXTERNAL_SCHEME = /^(mailto|tel|sms):/i

export const resolveNavTarget = (raw: string | null | undefined): NavTarget => {
  if (!raw) return null

  if (SAFE_EXTERNAL_SCHEME.test(raw)) return { url: raw, external: true }

  // Anchor (`#x`) and query-only (`?x`) links are unambiguously same-origin and
  // relative to the CURRENT document — resolve them against location.href (not
  // just the origin) so the current path is preserved, the way a bare
  // `window.location.href = '#x'` assignment behaved.
  if (raw.startsWith('#') || raw.startsWith('?')) {
    const origin = getOrigin()
    if (!origin) return null
    try {
      const url = new URL(raw, window.location.href)
      if (url.origin !== origin) return null
      return { url: `${url.pathname}${url.search}${url.hash}`, external: false }
    } catch {
      return null
    }
  }

  // 'www.x.com/promo' would parse as the in-app path '/www.x.com/promo' — only
  // a leading '/' or an explicit scheme is unambiguous.
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(raw)
  if (!raw.startsWith('/') && !hasScheme) {
    Sentry.captureException(
      new Error(`Ambiguous navigation link (no scheme, not a path): ${raw}`),
      { level: 'warning' }
    )
    return null
  }

  const url = toSafeHttpUrl(raw)
  if (!url) return null

  const origin = getOrigin()
  if (url.origin === origin) {
    return { url: `${url.pathname}${url.search}${url.hash}`, external: false }
  }
  return { url: url.href, external: true }
}

/**
 * Same-tab navigation to a validated CMS/config link. Resolves `raw` via
 * `resolveNavTarget` and, if valid, assigns `window.location.href`. Returns
 * `true` if it navigated, `false` if the link was rejected (so the caller can
 * fall back — e.g. dismiss a modal). Collapses the
 * `const t = resolveNavTarget(x); if (t) window.location.href = t.url` pattern
 * every config-driven `window.location` sink was repeating.
 */
export const navigateTo = (raw: string | null | undefined): boolean => {
  const target = resolveNavTarget(raw)
  if (!target) return false
  window.location.href = target.url
  return true
}

/**
 * Open a validated URL. http(s) opens in a new tab with its `opener` severed;
 * the safe handler schemes (`mailto:`/`tel:`/`sms:`) navigate same-tab so the
 * OS handler is invoked without spawning a phantom blank tab. Everything else
 * (`javascript:`/`data:`/malformed) is a no-op. Returns `true` only when it
 * actually opened/navigated — a rejected value OR a popup-blocked `window.open`
 * (returns null) yields `false`, so a caller can avoid advancing UI state
 * (e.g. "billing updated") when nothing happened.
 */
export const openExternal = (raw: string | null | undefined): boolean => {
  if (!raw) return false
  if (SAFE_EXTERNAL_SCHEME.test(raw)) {
    window.location.href = raw
    return true
  }
  const url = toSafeHttpUrl(raw)
  if (!url) return false
  // No 'noopener' in windowFeatures: with it, window.open returns null even on
  // success, indistinguishable from a blocked popup — and the return contract
  // above is what gates the wallet/billing flows (PRO-241). Severing the
  // handle manually keeps the same reverse-tabnabbing guarantee.
  const opened = window.open(url.href, '_blank')
  if (!opened) return false
  opened.opener = null
  return true
}
